import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, NeuralChatApiError } from "../api/client";
import { CodexPilotStatus, AgentExecutionSummary } from "../types";

type Props = { roomId: string; onStarted?: () => void };

const terminal = new Set(["SUCCEEDED", "FAILED", "CANCELLED", "TIMED_OUT"]);

export const CodexPilotPanel: React.FC<Props> = ({ roomId, onStarted }) => {
  const [status, setStatus] = useState<CodexPilotStatus | null>(null);
  const [execution, setExecution] = useState<{ jobId: string; value: AgentExecutionSummary } | null>(null);
  const [confirmations, setConfirmations] = useState({ non_sensitive: false, real_request: false, read_only: false });
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    controller.current?.abort();
    const next = new AbortController();
    controller.current = next;
    try {
      setStatus(await api.getCodexPilot(roomId, next.signal));
      setError("");
    } catch (value) {
      if (!(value instanceof DOMException && value.name === "AbortError")) setError(value instanceof NeuralChatApiError ? value.message : "파일럿 상태를 확인하지 못했습니다.");
    }
  }, [roomId]);

  useEffect(() => {
    void load();
    return () => controller.current?.abort();
  }, [load]);

  useEffect(() => {
    if (!execution || terminal.has(execution.value.status)) return undefined;
    const timer = window.setInterval(() => {
      void api.listAgentExecutions(execution.jobId).then((rows) => {
        const current = rows[0];
        if (current) setExecution({ jobId: execution.jobId, value: current });
      }).catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(timer);
  }, [execution]);

  const start = async () => {
    if (!status?.ready || Object.values(confirmations).some((value) => !value)) return;
    setBusy(true); setError("");
    try {
      const result = await api.startCodexPilot(roomId, { command_id: api.newCommandId(), confirmations: { non_sensitive: true, real_request: true, read_only: true } });
      setExecution({ jobId: result.job_id, value: result.execution });
      onStarted?.();
      setConfirmations({ non_sensitive: false, real_request: false, read_only: false });
    } catch (value) {
      setError(value instanceof NeuralChatApiError ? value.message : "파일럿을 시작하지 못했습니다.");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!execution || !reason.trim()) return;
    setBusy(true); setError("");
    try {
      const result = await api.cancelAgentExecution(execution.jobId, execution.value.id, { command_id: api.newCommandId(), reason: reason.trim() });
      setExecution({ jobId: execution.jobId, value: result.execution });
      setReason("");
    } catch (value) {
      setError(value instanceof NeuralChatApiError ? value.message : "파일럿을 중지하지 못했습니다.");
    } finally { setBusy(false); }
  };

  return <section aria-label="CODEX_READ_ONLY 파일럿" style={{ margin: "12px 0", padding: 16, border: "1px solid #c7d2fe", borderRadius: 12, background: "#eef2ff" }}>
    <h2 style={{ margin: "0 0 6px", fontSize: 16 }}>읽기 전용 Codex 파일럿</h2>
    <p style={{ margin: "0 0 10px", fontSize: 12, lineHeight: 1.6 }}>이유진 · company-docs · 고정된 외주 영업 준비 분석만 실행합니다. 자유 prompt, 파일 수정, 외부 조사, 고객 연락, 메일 발송은 제공하지 않습니다.</p>
    {status && <p style={{ margin: "6px 0", fontSize: 12 }} aria-live="polite">상태: {status.ready ? "READY" : "BLOCKED"} · 최대 {status.max_seconds}초 · control v{status.control_version}</p>}
    {error && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{error}</p>}
    {!execution && <div style={{ display: "grid", gap: 6, fontSize: 12 }}>
      <label><input type="checkbox" checked={confirmations.non_sensitive} onChange={(event) => setConfirmations((current) => ({ ...current, non_sensitive: event.target.checked }))} /> 비민감 자료만 포함되었음을 확인</label>
      <label><input type="checkbox" checked={confirmations.real_request} onChange={(event) => setConfirmations((current) => ({ ...current, real_request: event.target.checked }))} /> 실제 Codex 요청을 승인</label>
      <label><input type="checkbox" checked={confirmations.read_only} onChange={(event) => setConfirmations((current) => ({ ...current, read_only: event.target.checked }))} /> 읽기 전용이며 파일이 변경되지 않음을 확인</label>
      <button type="button" disabled={busy || !status?.ready || Object.values(confirmations).some((value) => !value)} onClick={() => void start()} style={{ width: "fit-content", marginTop: 5, padding: "8px 12px", border: 0, borderRadius: 8, background: "#4f46e5", color: "white", fontWeight: 700 }}>파일럿 실행</button>
    </div>}
    {execution && <div style={{ display: "grid", gap: 8, fontSize: 12 }}><strong>실행 상태: {execution.value.status}</strong>{!terminal.has(execution.value.status) && <><input value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} placeholder="중지 사유" /><button type="button" disabled={busy || !reason.trim()} onClick={() => void cancel()} style={{ width: "fit-content", padding: "8px 12px", border: 0, borderRadius: 8, background: "#b91c1c", color: "white", fontWeight: 700 }}>중지</button></>}</div>}
  </section>;
};
